import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useAnimationControls, useInView, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import useMotionPreference from "@/hooks/useMotionPreference";
import CodeSnippet from "./HeroCodeSnippet";

function HeroCodeWindow({ cinematic = false, handoffProgress }) {
  // Measure the stationary anchor so the animation cannot move its own scroll range.
  const ref = useRef(null);
  const reduceMotion = useMotionPreference();
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const [handoff, setHandoff] = useState({ x: -340, y: 0, scale: 4, exitX: -1800 });
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const entry = useAnimationControls();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 0.3, 0.55, 1], desktop ? [150, 0, 0, -100] : [36, 0, 0, -20]);
  const y = useTransform(scrollYProgress, [0, 0.3, 0.55, 1], desktop ? [40, 0, 0, -65] : [16, 0, 0, -20]);
  const rotate = useTransform(scrollYProgress, [0, 0.3, 0.55, 1], desktop ? [3, 0, 0, -4] : [1, 0, 0, -1]);
  const rotateY = useTransform(scrollYProgress, [0, 0.3, 0.55, 1], desktop ? [-14, 0, 0, 12] : [-4, 0, 0, 3]);
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.55, 1], desktop ? [0.94, 1, 1, 0.94] : [0.98, 1, 1, 0.98]);
  const opacity = useTransform(scrollYProgress, [0, 0.12, 0.85, 1], [0.65, 1, 1, 0.7]);
  const angle = useTransform(scrollYProgress, [0, 1], [120, 300]);
  const glow = useMotionTemplate`linear-gradient(${angle}deg, rgb(var(--brand-500) / 0.3), transparent 55%, rgb(var(--brand-500) / 0.12))`;
  const handoffX = useTransform(handoffProgress, [0, 0.2, 0.6, 0.78, 1], [0, 0, handoff.x, handoff.x, handoff.exitX]);
  const handoffY = useTransform(handoffProgress, [0, 0.2, 0.6, 0.78, 1], [0, 0, handoff.y, handoff.y, handoff.y - 80]);
  const handoffScale = useTransform(handoffProgress, [0, 0.2, 0.6, 0.78, 1], [1, 1, handoff.scale, handoff.scale, handoff.scale * 0.82]);
  const handoffRotateY = useTransform(handoffProgress, [0, 0.2, 0.6, 0.78, 1], [0, 0, -6, -6, -18]);
  const handoffOpacity = useTransform(handoffProgress, [0, 0.8, 0.98], [1, 1, 0]);
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const tiltX = useSpring(pointerX, { stiffness: 180, damping: 24 });
  const tiltY = useSpring(pointerY, { stiffness: 180, damping: 24 });

  useEffect(() => {
    if (reduceMotion) {
      entry.stop();
      entry.set({ opacity: 1, x: 0 });
    } else if (inView) {
      entry.start({ opacity: 1, x: 0 });
    }
  }, [entry, inView, reduceMotion]);

  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)");
    const update = () => setDesktop(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    pointerX.set(0);
    pointerY.set(0);
  }, [desktop, reduceMotion, pointerX, pointerY]);

  useLayoutEffect(() => {
    if (!cinematic || !ref.current) return;
    const anchor = ref.current;
    const measure = () => {
      const rect = anchor.getBoundingClientRect();
      const width = window.innerWidth;
      const height = window.innerHeight;
      setHandoff({
        x: width / 2 - (rect.left + rect.width / 2),
        y: height / 2 - (rect.top + rect.height / 2),
        scale: Math.max(width / Math.max(rect.width, 1), height / Math.max(rect.height, 1)) * 1.12,
        exitX: -width * 1.3,
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(anchor);
    window.addEventListener("resize", measure);
    measure();
    return () => { observer.disconnect(); window.removeEventListener("resize", measure); };
  }, [cinematic]);

  const resetPointer = () => { pointerX.set(0); pointerY.set(0); };

  return (
    <div
      ref={ref}
      data-testid="hero-code-window"
      className="relative z-20 col-span-12 mt-4 min-w-0 lg:col-span-5 lg:mt-0"
      onPointerMove={(event) => {
        if (reduceMotion || !desktop || event.pointerType !== "mouse" || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
        const rect = event.currentTarget.getBoundingClientRect();
        pointerX.set(Math.max(-3, Math.min(3, (0.5 - (event.clientY - rect.top) / rect.height) * 6)));
        pointerY.set(Math.max(-4, Math.min(4, ((event.clientX - rect.left) / rect.width - 0.5) * 8)));
      }}
      onPointerLeave={resetPointer}
      onPointerCancel={resetPointer}
    >
      <motion.div
        data-testid="hero-code-entry"
        initial={reduceMotion ? false : { opacity: 0, x: desktop ? 120 : 28 }}
        animate={entry}
        transition={{ duration: reduceMotion ? 0 : 0.9, delay: reduceMotion ? 0 : 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="[perspective:1200px]"
      >
        <motion.div
          data-testid="hero-code-scroll"
          className="[perspective:1000px]"
          style={reduceMotion ? { x: 0, y: 0, rotate: 0, rotateY: 0, scale: 1, opacity: 1 } : cinematic ? { x: handoffX, y: handoffY, rotateY: handoffRotateY, scale: handoffScale, opacity: handoffOpacity } : { x, y, rotate, rotateY, scale, opacity }}
        >
          <motion.div
            data-testid="hero-code-tilt"
            style={{ rotateX: reduceMotion || !desktop ? 0 : tiltX, rotateY: reduceMotion || !desktop ? 0 : tiltY }}
            className="relative rounded-3xl border border-white/10 bg-white/5 p-2 backdrop-blur-sm"
          >
            <motion.div aria-hidden="true" className="pointer-events-none absolute -inset-px rounded-3xl opacity-60" style={{ background: reduceMotion ? "linear-gradient(180deg, rgb(var(--brand-500) / 0.2), transparent)" : glow }} />
            <CodeSnippet />
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-xl border border-white/10 bg-black/60 px-4 py-3 backdrop-blur-md">
              <span className="font-mono text-xs text-white/70">~/tanishq/dev</span>
              <span className="flex items-center gap-1.5 font-mono text-xs text-brand-400">
                <span className="h-2 w-2 rounded-full bg-brand-500" /> building
              </span>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default memo(HeroCodeWindow);
